/** Browser-side PKCE + a tiny SSO self-test launcher (for the hive-web client). */

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomString(len = 32): string {
  return b64url(crypto.getRandomValues(new Uint8Array(len))).slice(0, len);
}

export async function createPkce(): Promise<{ verifier: string; challenge: string }> {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: b64url(new Uint8Array(digest)) };
}

const CLIENT_ID = "hive-web";

/** Kick off an Authorization Code + PKCE flow against Hive's own IdP. */
export async function startSso(): Promise<void> {
  const redirectUri = `${window.location.origin}/sso/callback`;
  const { verifier, challenge } = await createPkce();
  const state = randomString(24);
  const nonce = randomString(24);
  sessionStorage.setItem("sso_verifier", verifier);
  sessionStorage.setItem("sso_state", state);

  const url = new URL("/oidc/authorize", window.location.origin);
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid profile email org");
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  window.location.assign(url.toString());
}

export interface ExchangeResult {
  claims: Record<string, unknown>;
  raw: { access_token: string; id_token: string; token_type: string; expires_in: number; scope: string };
}

/** Exchange the code at the token endpoint (PKCE), returning decoded id_token claims. */
export async function exchangeCode(code: string): Promise<ExchangeResult> {
  const verifier = sessionStorage.getItem("sso_verifier");
  if (!verifier) throw new Error("Missing PKCE verifier (start the flow from this browser).");
  const redirectUri = `${window.location.origin}/sso/callback`;

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    client_id: CLIENT_ID,
    code_verifier: verifier,
  });
  const res = await fetch("/oidc/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error_description || json.error || "Token exchange failed");
  sessionStorage.removeItem("sso_verifier");
  sessionStorage.removeItem("sso_state");

  const claims = decodeJwt(json.id_token);
  return { claims, raw: json };
}

function decodeJwt(jwt: string): Record<string, unknown> {
  const part = jwt.split(".")[1] ?? "";
  const padded = part.replace(/-/g, "+").replace(/_/g, "/");
  try {
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return {};
  }
}
